import { useEffect } from 'react'
import { useLocation } from 'react-router-dom'

import { CANONICAL_ORIGIN, PUBLIC_ROUTES, seoForPath } from '@/lib/seo/routes'

/**
 * Keeps `<title>`, the meta description and the canonical link in step with
 * the current route on CLIENT-side navigation.
 *
 * Crawlers do not depend on this — they read the prerendered HTML that
 * `scripts/prerender.mjs` writes, which already carries the correct tags.
 * This exists so a human clicking around the SPA sees sensible tab titles and
 * so a shared URL copied mid-session is canonical.
 *
 * Renders null; mounted once inside the router in `App.tsx`.
 */
export function Seo() {
  const { pathname } = useLocation()

  useEffect(() => {
    // PUBLIC_ROUTES[0] is '/', the brand default — used for any route with no
    // entry of its own (every authenticated surface).
    const route = seoForPath(pathname) ?? PUBLIC_ROUTES[0]

    document.title = route.title
    upsertMeta('description', route.description)
    upsertCanonical(
      pathname === '/' ? `${CANONICAL_ORIGIN}/` : `${CANONICAL_ORIGIN}${pathname}`,
    )
  }, [pathname])

  return null
}

function upsertMeta(name: string, content: string) {
  let el = document.querySelector<HTMLMetaElement>(`meta[name="${name}"]`)
  if (!el) {
    el = document.createElement('meta')
    el.setAttribute('name', name)
    document.head.appendChild(el)
  }
  el.setAttribute('content', content)
}

function upsertCanonical(href: string) {
  let el = document.querySelector<HTMLLinkElement>('link[rel="canonical"]')
  if (!el) {
    el = document.createElement('link')
    el.setAttribute('rel', 'canonical')
    document.head.appendChild(el)
  }
  el.setAttribute('href', href)
}
